/**
 * RouteErrorBoundary — per-route error boundary component.
 *
 * Wraps each lazy-loaded route so a single page crash does not unmount the
 * entire application. The root Sentry.ErrorBoundary in App.tsx remains as the
 * last-resort catch-all; this boundary provides a friendlier, recoverable UI
 * at the route level.
 *
 * Usage (in AppRoutes.tsx):
 * ```tsx
 * <RouteErrorBoundary>
 *   <DashboardPage />
 * </RouteErrorBoundary>
 * ```
 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Fallback UI Component
// ---------------------------------------------------------------------------

interface RouteErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

const RouteErrorFallback: React.FC<RouteErrorFallbackProps> = ({ error, resetError }) => {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
        <AlertTriangle className="h-8 w-8 text-red-600" />
      </div>

      <h2 className="mb-2 text-xl font-bold text-gray-900">This page encountered an error</h2>
      <p className="mb-1 text-sm text-gray-500">
        The rest of the app is still running. You can try reloading this page or go back home.
      </p>

      {import.meta.env.DEV && error.message && (
        <pre className="mt-3 mb-4 max-w-lg overflow-x-auto rounded-lg bg-gray-100 p-3 text-left text-xs text-gray-600">
          {error.message}
        </pre>
      )}

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={() => { navigate('/dashboard'); resetError(); }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <Home className="h-4 w-4" />
          Go to Dashboard
        </button>
        <button
          type="button"
          onClick={resetError}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// RouteErrorBoundary — wraps Sentry.ErrorBoundary with the local fallback
// ---------------------------------------------------------------------------

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Per-route error boundary that catches rendering errors without bringing
 * down the whole SPA. Integrates with Sentry for automatic error reporting.
 */
export const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <RouteErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          resetError={resetError}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default RouteErrorBoundary;
